import { ValidationError } from './errors'

type BaseType = 'string' | 'number' | 'boolean' | 'any'

export const t = {
    string: (description: string): PropertySchema => ({
        type: 'string',
        description,
    }),
    number: (description: string): PropertySchema => ({
        type: 'number',
        description,
    }),
    boolean: (description: string): PropertySchema => ({
        type: 'boolean',
        description,
    }),
    enum: (description: string, values: string[]): PropertySchema => ({
        type: 'string',
        description,
        enum: values,
    }),
    array: (description: string, items: PropertySchema): PropertySchema => ({
        type: 'array',
        description,
        items,
    }),
    object: (
        description: string,
        properties: ObjectPropsSchema
    ): PropertySchema => ({
        type: 'object',
        description,
        properties,
    }),
    record: (
        description: string,
        key: BaseType,
        value: BaseType | PropertySchema
    ): PropertySchema => ({
        type: 'object',
        description,
        properties: {
            key: { type: key, description: 'The key of the record' },
            value:
                typeof value === 'string'
                    ? { type: value, description: 'The value of the record' }
                    : value,
        },
    }),
    /** Object that accepts any keys (property definitions). Use for schemas like {propName: {type, description}} */
    additionalProperties: (description: string): PropertySchema => ({
        type: 'object',
        description,
        additionalProperties: true,
    }),
    /** Discriminated union. Each schema in oneOf should have a discriminant (e.g. requestType). */
    oneOf: (description: string, schemas: PropertySchema[]): PropertySchema => ({
        type: 'oneOf',
        description,
        oneOf: schemas,
    }),
    empty: (): Empty => undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we need to allow any for the propsObjectDefinition
    propsObjectDefinition: (description: string): { [key: string]: any } => ({
        type: 'object | string | number | boolean | enum | array',
        description: description,
        properties: 'ObjectSchema',
        enum: 'string[]',
        items: 'string | number | boolean | enum | PropertySchema',
    }),
}

export type PropertySchema = {
    type: string
    description?: string
    items?: PropertySchema // if type == "array"
    properties?: ObjectPropsSchema // if type == "object"
    enum?: string[] // if type == "enum"
    optional?: boolean
    filterable?: boolean
    hint?: string
    /** When true, object accepts any keys (no property validation) */
    additionalProperties?: boolean
    /** For oneOf: array of alternative schemas */
    oneOf?: PropertySchema[]
}

export type InferType<T extends ObjectPropsSchema> = {
    [K in keyof T]: T[K] extends { optional: true }
        ? InferPropertyType<T[K]> | undefined
        : InferPropertyType<T[K]>
}

export type FilterType<T extends ObjectPropsSchema> = {
    [K in keyof T as T[K] extends { filterable: true }
        ? K
        : never]?: InferPropertyType<T[K]>
}

export type InferPropertyType<T extends PropertySchema> =
    T['type'] extends 'string'
        ? string
        : T['type'] extends 'number'
          ? number
          : T['type'] extends 'boolean'
            ? boolean
            : T['type'] extends 'array'
              ? T['items'] extends PropertySchema
                  ? InferPropertyType<T['items']>[]
                  : any[]
              : T['type'] extends 'object'
                ? T['properties'] extends ObjectPropsSchema
                    ? InferType<T['properties']>
                    : any
                : T['type'] extends 'enum'
                  ? T['enum'] extends string[]
                      ? T['enum'][number]
                      : string
                  : any

export const filterable = (p: PropertySchema): PropertySchema => {
    p.filterable = true
    return p
}

export const optional = (p: PropertySchema): PropertySchema => {
    p.optional = true
    return p
}

export const hint = (p: PropertySchema, text: string): PropertySchema => {
    p.hint = text
    return p
}

export type Empty = undefined
export type ObjectPropsSchema = {
    [key: string]: PropertySchema
}

export type ValidationResult = {
    success: boolean
    errors?: {
        [key: string]: string[]
    }
}

export const schemaToJSONString = (schema: ObjectPropsSchema): string => {
    return JSON.stringify(schema, null, 2)
}

/**
 * Converts a property schema (as built with `t.*` / `optional()`) to JSON Schema draft-07 shape
 * for LLM prompts. Used when task response schemas are not Zod instances.
 */
export function propertySchemaToJsonSchema(
    prop: PropertySchema
): Record<string, unknown> {
    const base: Record<string, unknown> = {}
    if (prop.description) base.description = prop.description

    if (prop.type === 'string' && prop.enum && prop.enum.length > 0) {
        return { ...base, type: 'string', enum: prop.enum }
    }
    if (
        prop.type === 'string' ||
        prop.type === 'number' ||
        prop.type === 'boolean'
    ) {
        return { ...base, type: prop.type }
    }
    if (prop.type === 'array') {
        if (!prop.items) {
            return { ...base, type: 'array', items: {} }
        }
        if (typeof prop.items === 'string') {
            return {
                ...base,
                type: 'array',
                items: { type: prop.items },
            }
        }
        return {
            ...base,
            type: 'array',
            items: propertySchemaToJsonSchema(prop.items),
        }
    }
    if (prop.type === 'object') {
        if (prop.additionalProperties) {
            return { ...base, type: 'object', additionalProperties: true }
        }
        if (prop.properties) {
            const jsonProps: Record<string, unknown> = {}
            const required: string[] = []
            for (const [k, v] of Object.entries(prop.properties)) {
                jsonProps[k] = propertySchemaToJsonSchema(v)
                if (!v.optional) required.push(k)
            }
            return {
                ...base,
                type: 'object',
                properties: jsonProps,
                ...(required.length > 0 ? { required } : {}),
            }
        }
        return { ...base, type: 'object' }
    }
    if (prop.type === 'oneOf' && prop.oneOf && prop.oneOf.length > 0) {
        return {
            ...base,
            oneOf: prop.oneOf.map((s) => propertySchemaToJsonSchema(s)),
        }
    }
    return { ...base, type: prop.type }
}

/** Root response shape `{ entities: ... }` → JSON Schema object with `properties` / `required`. */
export function objectPropsSchemaToJsonSchema(
    schema: ObjectPropsSchema
): Record<string, unknown> {
    const properties: Record<string, unknown> = {}
    const required: string[] = []
    for (const [key, prop] of Object.entries(schema)) {
        properties[key] = propertySchemaToJsonSchema(prop)
        if (!prop.optional) required.push(key)
    }
    return {
        type: 'object',
        properties,
        ...(required.length > 0 ? { required } : {}),
    }
}

const validatePropertySchemaFieldsAreCorrect = (
    schema: PropertySchema
): boolean => {
    for (const key in Object.keys(schema)) {
        const property = Object.values(schema)[key]

        if (key === 'properties') {
            if (typeof property !== 'object') {
                return false
            }
            return (
                property &&
                validatePropertySchemaFieldsAreCorrect(
                    property as PropertySchema
                )
            )
        }
        if (key === 'items') {
            if (typeof property !== 'object') {
                return false
            }
            return (
                property &&
                validatePropertySchemaFieldsAreCorrect(
                    property as PropertySchema
                )
            )
        }
        if (key === 'enum') {
            return (
                Array.isArray(property) &&
                property.every((value) => typeof value === 'string')
            )
        }
        if (key === 'type') {
            if (typeof property !== 'string') {
                return false
            }
            return [
                'string',
                'number',
                'boolean',
                'array',
                'object',
                'enum',
            ].includes(property as string)
        }
    }
    return true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- we need to allow any for the propsObjectDefinition
export const validateIsPropertySchema = (
    schema: any
): schema is PropertySchema => {
    return (
        typeof schema === 'object' &&
        schema.type &&
        ['string', 'number', 'boolean', 'array', 'object', 'enum'].includes(
            schema.type
        ) &&
        validatePropertySchemaFieldsAreCorrect(schema)
    )
}

export const validateIsTypeSchema = (
    schema: ObjectPropsSchema
): ValidationResult => {
    const errors: { [key: string]: string[] } = {}
    for (const key in schema) {
        if (!(typeof schema[key] === 'object')) {
            errors[key] = [
                `Invalid schema for ${key} - must be an object, got ${typeof schema[key]}`,
            ]
            continue
        }
        const property = schema[key]
        if (
            ![
                'string',
                'number',
                'boolean',
                'array',
                'object',
                'enum',
            ].includes(property.type)
        ) {
            errors[key] = [`Invalid type: ${property.type}`]
        }
        if (property.type === 'array') {
            if (!validateIsPropertySchema(property.items)) {
                errors[key] = errors[key] || []
                errors[key].push(
                    `Invalid items schema: ${JSON.stringify(property.items)}`
                )
            }
        }
        if (property.type === 'enum') {
            if (!Array.isArray(property.enum) || property.enum.length === 0) {
                errors[key] = errors[key] || []
                errors[key].push(`Enum must be a non-empty array`)
            }

            if (
                !property.enum ||
                property.enum.some((value) => typeof value !== 'string')
            ) {
                errors[key] = errors[key] || []
                errors[key].push(`Enum values must be strings`)
            }
        }

        if (property.type === 'object' && property.properties) {
            const validationResult = validateIsTypeSchema(property.properties)
            if (!validationResult.success) {
                for (const errorKey in validationResult.errors) {
                    errors[`${key}.${errorKey}`] =
                        validationResult.errors[errorKey]!
                }
            }
        }
    }
    return { success: Object.keys(errors).length === 0, errors }
}

/**
 * Safely merges a schema extension into an existing schema.
 * This function:
 * - Adds new properties that don't exist
 * - Extends existing object properties by merging nested properties
 * - Extends enum properties by adding new enum values
 * - Never deletes or changes existing property types
 * - Never makes optional properties required
 * 
 * @param existing The existing schema properties
 * @param extension The properties to add or extend
 * @returns The merged schema properties
 * @throws ValidationError if the extension would break existing properties
 */
export const mergeSchemaExtension = (
    existing: ObjectPropsSchema,
    extension: ObjectPropsSchema
): ObjectPropsSchema => {
    const existingProperties = { ...existing }
    const extensionProperties = { ...extension }
    const mergedProperties: ObjectPropsSchema = { ...existingProperties }

    // Process each property in the extension
    for (const [key, extensionProp] of Object.entries(extensionProperties)) {
        // Skip key and name as they are system-managed
        if (key === 'key' || key === 'name') {
            continue
        }

        const existingProp = existingProperties[key]

        if (!existingProp) {
            // New property - add it
            mergedProperties[key] = { ...extensionProp }
        } else {
            // Existing property - extend it safely
            if (existingProp.type !== extensionProp.type) {
                throw new ValidationError(
                    `Cannot change type of existing property '${key}' from '${existingProp.type}' to '${extensionProp.type}'. Schema extensions can only add new properties or extend existing ones.`,
                    { [key]: [`Type mismatch: existing is '${existingProp.type}', extension is '${extensionProp.type}'`] }
                )
            }

            // Merge based on type
            if (extensionProp.type === 'object' && extensionProp.properties) {
                // For objects, merge nested properties
                const existingNested = existingProp.properties || {}
                const extensionNested = extensionProp.properties || {}
                const mergedNested: ObjectPropsSchema = { ...existingNested }

                for (const [nestedKey, nestedProp] of Object.entries(extensionNested)) {
                    if (existingNested[nestedKey]) {
                        // Nested property exists - check for conflicts
                        if (existingNested[nestedKey].type !== nestedProp.type) {
                            throw new ValidationError(
                                `Cannot change type of nested property '${key}.${nestedKey}' from '${existingNested[nestedKey].type}' to '${nestedProp.type}'.`,
                                { [`${key}.${nestedKey}`]: ['Type mismatch in nested property'] }
                            )
                        }
                        // Merge nested property (keep existing, update description if provided)
                        mergedNested[nestedKey] = {
                            ...existingNested[nestedKey],
                            description: nestedProp.description || existingNested[nestedKey].description,
                        }
                    } else {
                        // New nested property
                        mergedNested[nestedKey] = { ...nestedProp }
                    }
                }

                mergedProperties[key] = {
                    ...existingProp,
                    properties: mergedNested,
                    description: extensionProp.description || existingProp.description,
                }
            } else if (extensionProp.type === 'enum' && extensionProp.enum) {
                // For enums, merge enum values (add new ones, keep existing)
                const existingEnum = existingProp.enum || []
                const extensionEnum = extensionProp.enum || []
                const mergedEnum = [...new Set([...existingEnum, ...extensionEnum])]

                mergedProperties[key] = {
                    ...existingProp,
                    enum: mergedEnum,
                    description: extensionProp.description || existingProp.description,
                }
            } else {
                // For other types (string, number, boolean, array), only update description
                // Don't allow changing items for arrays or other properties
                if (extensionProp.type === 'array' && extensionProp.items) {
                    // Check if items type matches
                    const existingItems = existingProp.items
                    if (existingItems && extensionProp.items.type !== existingItems.type) {
                        throw new ValidationError(
                            `Cannot change items type of array property '${key}' from '${existingItems.type}' to '${extensionProp.items.type}'.`,
                            { [key]: ['Array items type mismatch'] }
                        )
                    }
                }

                // Only update description, keep everything else
                mergedProperties[key] = {
                    ...existingProp,
                    description: extensionProp.description || existingProp.description,
                }
            }

            // Ensure optional status doesn't become more restrictive
            if (existingProp.optional === true && extensionProp.optional === false) {
                throw new ValidationError(
                    `Cannot make optional property '${key}' required. Schema extensions cannot make properties more restrictive.`,
                    { [key]: ['Cannot change optional to required'] }
                )
            }
        }
    }

    return mergedProperties
}

export const validateObject = (
    schema: ObjectPropsSchema,
    data: unknown
): ValidationResult => {
    for (const key in schema) {
        const propertySchema = schema[key]!

        if (
            !propertySchema.optional &&
            !Object.prototype.hasOwnProperty.call(data, key)
        ) {
            return {
                success: false,
                errors: { [key]: ['Property is missing from object'] },
            }
        }

        if (
            propertySchema.optional &&
            !Object.prototype.hasOwnProperty.call(data, key)
        ) {
            continue
        }

        // @ts-ignore - we know that data is an object and has this key
        const property = data[key]
        const validationResult = validateProperty(key, propertySchema, property)
        if (!validationResult.success) {
            return validationResult
        }
    }
    // If we get here, all properties are valid
    return { success: true }
}

const validateProperty = (
    key: string,
    propertySchema: PropertySchema,
    property: unknown
): ValidationResult => {
    if (propertySchema.type === 'oneOf') {
        if (!propertySchema.oneOf || propertySchema.oneOf.length === 0) {
            return { success: false, errors: { [key]: ['oneOf has no schemas'] } }
        }
        for (const schema of propertySchema.oneOf) {
            const result = validateProperty(key, schema, property)
            if (result.success) return result
        }
        return {
            success: false,
            errors: { [key]: ['Value does not match any of the allowed schemas'] },
        }
    }

    if (propertySchema.type === 'array') {
        if (!Array.isArray(property)) {
            return {
                success: false,
                errors: { [key]: ['Property is not an array'] },
            }
        }

        if (property.length === 0) {
            return { success: true }
        }

        if (propertySchema.items) {
            switch (typeof propertySchema.items) {
                case 'string': {
                    // BaseType
                    if (typeof property[0] !== propertySchema.items) {
                        return {
                            success: false,
                            errors: {
                                [key]: [
                                    `Property is not a ${propertySchema.items}, it is a ${typeof property[0]}`,
                                ],
                            },
                        }
                    } else {
                        return { success: true }
                    }
                }
                case 'object': {
                    // ObjectSchema
                    for (const item of property) {
                        const validationResult = validateProperty(
                            key,
                            propertySchema.items,
                            item
                        )
                        if (!validationResult.success) {
                            return {
                                success: false,
                                errors: {
                                    ...validationResult.errors,
                                    ...{
                                        key: [
                                            `Property is not valid (${item})`,
                                        ],
                                    },
                                },
                            }
                        }
                    }
                }
            }
        }
    } else if (propertySchema.type === 'object') {
        if (
            typeof property !== 'object' ||
            property === null ||
            Array.isArray(property)
        ) {
            return {
                success: false,
                errors: {
                    [key]: [
                        `Property is not an object - instead it is a ${typeof property}`,
                    ],
                },
            }
        }

        if (propertySchema.additionalProperties) {
            return { success: true }
        }

        if (propertySchema.properties) {
            const validationResult = validateObject(
                propertySchema.properties,
                property
            )
            if (!validationResult.success) {
                return {
                    success: false,
                    errors: {
                        ...validationResult.errors,
                        ...{ key: ['Property is not valid'] },
                    },
                }
            }
        }
    } else if (propertySchema.type === 'enum') {
        if (typeof property !== 'string') {
            return {
                success: false,
                errors: { [key]: ['Enum property is not a string'] },
            }
        } else {
            if (!propertySchema.enum?.includes(property as string)) {
                return {
                    success: false,
                    errors: {
                        [key]: [
                            `Property ${property} is not in the enum (${propertySchema.enum})`,
                        ],
                    },
                }
            }
            return { success: true }
        }
    } else {
        if (typeof property !== propertySchema.type) {
            if (property === undefined && propertySchema.optional) {
                return { success: true }
            }
            return {
                success: false,
                errors: {
                    [key]: [
                        `Property is not a ${propertySchema.type} (instead it was a ${typeof property})`,
                    ],
                },
            }
        }
    }

    return { success: true }
}
